import React, { useEffect, Suspense } from 'react';
import { Box, Typography, Container, Divider } from '@mui/material';
import Carousel from '../components/Carousel';
import OptimizedImage from '../components/OptimizedImage';
import { LazyContactForm, LoadingFallback } from '../components/LazyComponents';
import { preloadImages } from '../utils/imageOptimization';

// Import images from src folder
import cloudstruck from '../images/cloudstruck.jpeg';
import tankhaul from '../images/newtruck.png';
import twotruck from '../images/twotruckhaul.jpeg';
import nclogo from '../images/nclogo.jpg';

const Home = () => {
  const images = [cloudstruck, tankhaul, twotruck]; // Use imported image paths

  useEffect(() => {
    // Preload critical images for faster rendering
    preloadImages([cloudstruck, tankhaul, twotruck, nclogo]);

    // Add structured data for the organization
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "HDF Hauling",
      "description": "Professional trucking fleet management, dispatch services, and reliable freight hauling across the country",
      "url": "https://hdfhauling.com",
      "logo": "https://hdfhauling.com/static/media/logohdf.jpeg",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "areaServed": "US",
        "availableLanguage": "English"
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      },
      "sameAs": [],
      "service": [
        {
          "@type": "Service",
          "name": "Fleet Management Services",
          "description": "Expert dispatching services with 24/7 support, maximized earnings, and personalized solutions"
        },
        {
          "@type": "Service", 
          "name": "Hauling Services",
          "description": "Reliable freight delivery including hotshot loads, flatbed with ramps, and specialty loads"
        },
        {
          "@type": "Service",
          "name": "Getting Started in Trucking",
          "description": "Business startup assistance, CDL guidance, fleet consulting, and ongoing support for new trucking entrepreneurs"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <Box component="main" sx={{ minHeight: '100vh' }}>
      {/* Hero Carousel Section */}
      <section aria-label="Company showcase">
        <Box
          sx={{
            height: { xs: '300px', sm: '400px', md: '500px' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Carousel images={images} />
        </Box>
      </section>

      {/* Main Content Section */}
      <Box
        sx={{
          background: 'black',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          {/* Hero Text Section */}
          <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
            <Typography 
              component="h1" 
              variant="h3" 
              sx={{
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mb: 3,
                background: 'linear-gradient(45deg, #ff0000, #ff4444)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2,
              }}
            >
              Professional Trucking Fleet Management & Dispatch Services
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{
                color: 'rgba(255,255,255,0.9)',
                maxWidth: '900px',
                mx: 'auto',
                lineHeight: 1.7,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
              }}
            >
              At HDF Hauling, we partner with trucking fleets to maximize utilization and deliver every load on time. 
              Our advanced fleet management streamlines routes, maintenance, and compliance—boosting efficiency and profitability.
            </Typography>
          </Box>

          {/* Certification Section */}
          <Box 
            textAlign="center" 
            mb={{ xs: 6, md: 8 }}
            sx={{
              py: 4,
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography 
              variant="h5" 
              color="white" 
              sx={{ 
                mb: 3,
                fontWeight: 600,
              }}
            >
              North Carolina Certified Company
            </Typography>
            
            <Box 
              sx={{
                display: 'flex',
                justifyContent: 'center',
                maxWidth: '300px',
                mx: 'auto',
              }}
            >
              <OptimizedImage
                src={nclogo}
                alt="North Carolina certified trucking company logo"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}
                priority={false}
              />
            </Box>
          </Box>

          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: { xs: 4, md: 6 } }} />

          {/* Contact Form Section */}
          <Box 
            sx={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
            }}
          >
            {/* Form Header */}
            <Box 
              sx={{
                textAlign: 'center',
                py: { xs: 4, md: 6 },
                px: { xs: 2, md: 4 },
                background: 'rgba(255,0,0,0.1)',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <Typography 
                component="h2" 
                variant="h4" 
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography 
                variant="h6" 
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                }}
              >
                Partner with HDF Hauling today. Fill out the form below to learn how our professional services 
                can drive your trucking business forward.
              </Typography>
            </Box>

            {/* Contact Form */}
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <Suspense fallback={<LoadingFallback message="Loading contact form..." />}>
                <LazyContactForm />
              </Suspense>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;